(() => {
  const SELECTOR = '.pdp-yotpo-reviews__content[data-yotpo-auth-url][data-yotpo-reviews-url]';

  const MOCK_PAYLOAD = {
    response: {
      bottomline: {
        average_score: 0,
        total_review: 0,
        custom_fields_bottomline: {},
      },
      reviews: [],
    },
  };

  const formatDate = (isoString) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_) {
      return '';
    }
  };

  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const renderStars = (score, max = 5) => {
    const s = clamp(Number(score) || 0, 0, max);
    const full = Math.floor(s);
    const half = s - full >= 0.5 ? 1 : 0;
    const empty = max - full - half;

    const star = (type) => {
      if (type === 'full') return '<span class="pdp-yotpo-star pdp-yotpo-star--full">★</span>';
      if (type === 'half') return '<span class="pdp-yotpo-star pdp-yotpo-star--half">★</span>';
      return '<span class="pdp-yotpo-star pdp-yotpo-star--empty">★</span>';
    };

    return `${Array.from({ length: full }).map(() => star('full')).join('')}${
      half ? star('half') : ''
    }${Array.from({ length: empty }).map(() => star('empty')).join('')}`;
  };

  const normalizeFitLabel = (fitValue) => {
    const v = Number(fitValue);
    if (!Number.isFinite(v)) return '';
    if (v <= 1) return 'Runs Small';
    if (v === 2) return 'True to size';
    return 'Runs Large';
  };

  const normalizeQualityLabel = (qValue) => {
    const v = Number(qValue);
    if (!Number.isFinite(v)) return '';
    if (v <= 2) return 'Low';
    if (v === 3) return 'Standard';
    return 'High';
  };

  const renderRange = ({ title, minLabel, midLabel, maxLabel, value, min, max, showMid = true, tickCount }) => {
    const v = clamp(Number(value) || 0, min, max);
    const pct = ((v - min) / (max - min)) * 100;

    const resolvedTickCount = Number.isFinite(Number(tickCount)) ? Number(tickCount) : null;
    const ticks = (() => {
      if (resolvedTickCount && resolvedTickCount >= 2) {
        const selectedIdx = clamp(
          Math.round(((v - min) / (max - min)) * (resolvedTickCount - 1)),
          0,
          resolvedTickCount - 1
        );
        return Array.from({ length: resolvedTickCount })
          .map((_, idx) => {
            const tPct = (idx / (resolvedTickCount - 1)) * 100;
            const active = idx === selectedIdx ? ' is-active' : '';
            return `<span class="pdp-yotpo-range__tick${active}" style="left:${tPct}%"></span>`;
          })
          .join('');
      }

      const stepCount = Math.max(0, Math.floor(max) - Math.ceil(min));
      const selectedStep = clamp(Math.round(v), Math.ceil(min), Math.floor(max));
      return Array.from({ length: stepCount + 1 })
        .map((_, idx) => {
          const t = Math.ceil(min) + idx;
          const tPct = ((t - min) / (max - min)) * 100;
          const active = t === selectedStep ? ' is-active' : '';
          return `<span class="pdp-yotpo-range__tick${active}" style="left:${tPct}%"></span>`;
        })
        .join('');
    })();

    return `
      <div class="pdp-yotpo-range">
        <div class="pdp-yotpo-range__title F1-Reg-LC">${escapeHtml(title)}</div>
        <div class="pdp-yotpo-range__track" role="meter" aria-valuemin="${min}" aria-valuemax="${max}" aria-valuenow="${v}">
          ${ticks}
          <div class="pdp-yotpo-range__marker" style="left:${pct}%"></div>
        </div>
        <div class="pdp-yotpo-range__labels F1-Reg-UC">
          <span>${escapeHtml(minLabel)}</span>
          ${showMid && midLabel ? `<span>${escapeHtml(midLabel)}</span>` : ''}
          <span>${escapeHtml(maxLabel)}</span>
        </div>
      </div>
    `;
  };

  const getToken = async (authUrl) => {
    const cacheKey = `pdp_yotpo_token_${authUrl}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;

    const res = await fetch(authUrl, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Auth failed: ${res.status}`);
    const data = await res.json();
    const token = data.access_token || data.token || data?.response?.access_token || data?.response?.token;
    if (!token) throw new Error('Auth token missing in response');
    sessionStorage.setItem(cacheKey, token);
    return token;
  };

  const getReviews = async ({ reviewsUrl, appKey, productId, token }) => {
    const url = new URL(reviewsUrl, window.location.origin);
    if (productId) url.searchParams.set('product_id', String(productId));
    if (appKey) url.searchParams.set('app_key', String(appKey));

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: token ? { 'X-Yotpo-Token': token } : undefined,
    });
    if (!res.ok) throw new Error(`Reviews fetch failed: ${res.status}`);
    return res.json();
  };

  const getMockPayload = async ({ container }) => {
    const url = container.getAttribute('data-yotpo-mock-payload-url');
    if (!url) return MOCK_PAYLOAD;

    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return MOCK_PAYLOAD;
    const json = await res.json();
    if (!json || typeof json !== 'object') return MOCK_PAYLOAD;
    return json;
  };

  const parseList = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;

    // JSON array support
    if (s.startsWith('[')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((x) => String(x).trim()).filter(Boolean);
      } catch (_) {
        return null;
      }
    }

    // Comma-separated support
    return s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const render = (root, payload, config) => {
    const left = root.querySelector('[data-yotpo-left]');
    const right = root.querySelector('[data-yotpo-right]');
    if (!left || !right) return;

    const response = payload?.response;
    const bottomline = response?.bottomline;
    const reviews = Array.isArray(response?.reviews) ? response.reviews : [];
    const first = reviews[0];

    if (!bottomline || !first) {
      left.innerHTML = '';
      right.innerHTML = '';
      return;
    }

    const avg = Number(bottomline.average_score || 0);
    const total = Number(bottomline.total_review || 0);

    const fitMatchTitle = config?.fit?.title || 'Fit Expectation';
    const qualityMatchTitle = config?.quality?.title || 'Quality';

    const fitKey = Object.keys(bottomline.custom_fields_bottomline || {}).find(
      (k) => bottomline.custom_fields_bottomline?.[k]?.title === fitMatchTitle
    );
    const qualityKey = Object.keys(bottomline.custom_fields_bottomline || {}).find(
      (k) => bottomline.custom_fields_bottomline?.[k]?.title === qualityMatchTitle
    );

    let fit = fitKey ? bottomline.custom_fields_bottomline[fitKey] : null;
    let quality = qualityKey ? bottomline.custom_fields_bottomline[qualityKey] : null;

    // Fallback: calculate averages from individual reviews if custom_fields_bottomline is empty
    if (!fit || !quality) {
      const fitValues = [];
      const qualityValues = [];
      
      reviews.forEach((review) => {
        const cf = review.custom_fields || {};
        Object.values(cf).forEach((field) => {
          if (field?.title === fitMatchTitle && typeof field.value === 'number') {
            fitValues.push(field.value);
          }
          if (field?.title === qualityMatchTitle && typeof field.value === 'number') {
            qualityValues.push(field.value);
          }
        });
      });

      if (!fit && fitValues.length > 0) {
        const avgFit = fitValues.reduce((a, b) => a + b, 0) / fitValues.length;
        fit = { title: fitMatchTitle, average_score: avgFit };
      }
      if (!quality && qualityValues.length > 0) {
        const avgQuality = qualityValues.reduce((a, b) => a + b, 0) / qualityValues.length;
        quality = { title: qualityMatchTitle, average_score: avgQuality };
      }
    }

    const cf = first.custom_fields || {};
    const cfEntries = Object.values(cf);
    const getCfValue = (title) => cfEntries.find((x) => x?.title === title)?.value;

    const age = getCfValue('Age');
    const height = getCfValue('Height');
    const sizePurchased = getCfValue('Size Purchased');
    const usualSize = getCfValue('Usual Size?');

    left.innerHTML = `
      <div class="pdp-yotpo-review-card">
        <div class="pdp-yotpo-review-card__top">
          <div class="pdp-yotpo-review-card__user">
            <div class="pdp-yotpo-review-card__name F1-Reg-LC">${escapeHtml(first.user?.display_name || '')}</div>
            <div class="pdp-yotpo-review-card__verified F2-Reg-LC">${first.verified_buyer ? 'Verified Buyer' : ''}</div>
          </div>
          <div class="pdp-yotpo-review-card__rating">
            <div class="pdp-yotpo-stars">${renderStars(first.score)}</div>
          </div>
          <div class="pdp-yotpo-review-card__date F2-Reg-LC">${escapeHtml(formatDate(first.created_at))}</div>
        </div>

        <div class="pdp-yotpo-review-card__content">
          <div class="pdp-yotpo-review-card__text B1-Medium">${escapeHtml(first.content || '')}</div>
        </div>

        <div class="pdp-yotpo-review-card__meta">
          <div class="pdp-yotpo-review-card__meta__item">
            ${age ? `<div class="F1-Reg-LC"><span class="F1-Reg-LC">Age:</span> ${escapeHtml(age)}</div>` : ''}
            ${height ? `<div class="F1-Reg-LC"><span class="F1-Reg-LC">Height:</span> ${escapeHtml(height)}</div>` : ''}            
          </div>
          <div class="pdp-yotpo-review-card__meta__item">
            ${sizePurchased ? `<div class="F1-Reg-LC"><span class="F1-Reg-LC">Size:</span> ${escapeHtml(sizePurchased)}</div>` : ''}
            ${usualSize ? `<div class="F1-Reg-LC"><span class="F1-Reg-LC">Usual Size:</span> ${escapeHtml(usualSize)}</div>` : ''}           
          </div>
        </div>
      </div>
    `;

    right.innerHTML = `
      <div class="pdp-yotpo-summary">
        <div class="pdp-yotpo-summary__rating">
          <div class="pdp-yotpo-summary__stars">${renderStars(avg)}</div>
          <div class="F1-Reg-UC">${avg.toFixed(1)}</div>
          <div class="pdp-yotpo-summary__count F1-Medium-LC">(${total} reviews)</div>
        </div>

        ${
          fit
            ? renderRange({
                title: config?.fit?.title || fit.title,
                minLabel: config?.fit?.minLabel || 'Runs Small',
                midLabel: config?.fit?.midLabel || 'True to size',
                maxLabel: config?.fit?.maxLabel || 'Runs Large',
                showMid: config?.fit?.showMid,
                tickCount: config?.fit?.tickCount,
                value: fit.average_score,
                min: 0,
                max: 3,
              })
            : ''
        }

        ${
          quality
            ? renderRange({
                title: config?.quality?.title || quality.title,
                minLabel: config?.quality?.minLabel || 'Low',
                midLabel: config?.quality?.midLabel || 'Standard',
                maxLabel: config?.quality?.maxLabel || 'High',
                showMid: config?.quality?.showMid,
                tickCount: config?.quality?.tickCount,
                value: quality.average_score,
                min: 0,
                max: 5,
              })
            : ''
        }

        <button type="button" class="secondary-composed" data-yotpo-all>
          All reviews
        </button>
      </div>
    `;

    const allBtn = right.querySelector('[data-yotpo-all]');
    if (allBtn) {
      allBtn.addEventListener('click', () => {
        const drawerTrigger = document.querySelector('.pdp-drawer-trigger-reviews');
        if (drawerTrigger) {
          drawerTrigger.click();
          return;
        }

        const yotpoSection = document.querySelector('#yotpo-reviews') || document.querySelector('.yotpo-main-widget');
        if (yotpoSection) yotpoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const init = async (container) => {
    const root = container.querySelector('[data-yotpo-root]');
    if (!root) return;

    const authUrl = container.getAttribute('data-yotpo-auth-url');
    const reviewsUrl = container.getAttribute('data-yotpo-reviews-url');
    const appKey = container.getAttribute('data-yotpo-app-key');
    const productId = container.getAttribute('data-yotpo-product-id');

    const fitList = parseList(container.getAttribute('data-yotpo-fit'));
    const qualityList = parseList(container.getAttribute('data-yotpo-quality'));

    const listToConfig = (arr, defaults) => {
      if (!Array.isArray(arr) || arr.length < 3) return defaults;

      // Support both formats:
      // - [min, mid, max] (or any length labels list)
      // - [title, ...labels]
      //
      // Disambiguation: treat the first element as a title ONLY when it matches
      // the expected Yotpo field title (defaults.title). Otherwise, assume it's
      // a labels-only list.
      const first = String(arr[0] || '').trim();
      const hasTitle =
        arr.length >= 4 &&
        first &&
        String(defaults.title || '').trim().toLowerCase() === first.toLowerCase();

      const title = hasTitle ? first : defaults.title;
      const labels = hasTitle ? arr.slice(1) : arr;

      if (labels.length < 2) return defaults;
      const minLabel = labels[0];
      const maxLabel = labels[labels.length - 1];

      const showMid = labels.length % 2 === 1;
      const midLabel = showMid ? labels[Math.floor(labels.length / 2)] : '';

      return { title, minLabel, midLabel, maxLabel, showMid, tickCount: labels.length };
    };

    const sliderConfig = {
      fit: listToConfig(fitList, {
        title: 'Fit Expectation',
        minLabel: 'Runs Small',
        midLabel: 'True to size',
        maxLabel: 'Runs Large',
        tickCount: 4,
      }),
      quality: listToConfig(qualityList, {
        title: 'Quality',
        minLabel: 'Low',
        midLabel: 'Standard',
        maxLabel: 'High',
        tickCount: 6,
      }),
    };

    const qs = new URLSearchParams(window.location.search);
    const mockEnabled =
      container.getAttribute('data-yotpo-mock') === 'true' ||
      container.hasAttribute('data-yotpo-mock') ||
      qs.get('yotpo_mock') === '1';

    if (mockEnabled) {
      try {
        root.classList.add('is-loading');
        const payload = await getMockPayload({ container });
        render(root, payload, sliderConfig);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        root.classList.remove('is-loading');
      }
      return;
    }

    try {
      root.classList.add('is-loading');
      const token = await getToken(authUrl);
      const payload = await getReviews({ reviewsUrl, appKey, productId, token });
      render(root, payload, sliderConfig);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      root.classList.remove('is-loading');
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      init(el);
    });
  });
})();
